import clsx from "clsx";
import { array, bool, func, string } from "prop-types";
import {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  resetDocumentState,
  setDocumentMetadata,
  setIsPanelOpen,
} from "slices/documentViewer/documentViewerSlice";
import {
  selectCurrentPage,
  selectIsPanelOpen,
  selectTotalPages,
} from "slices/documentViewer/selectors";
import { getFileExtension } from "utils/getFileFormat";

import { FILE_PROP_TYPE } from "./constants";
import classes from "./DocumentViewer.module.scss";
import DocumentViewerContext from "./DocumentViewerContext";
import DocXDocument from "./DocXDocument";
import PageNavigation from "./PageNavigation";
import PdfDocument from "./PdfDocument";
import Toolbar from "./Toolbar";

const DocumentViewer = forwardRef(
  (
    {
      canToggleNarrow = false,
      className,
      file,
      isNarrow = false,
      showFileName = false,
      showHeader = true,
      showNavigation = true,
      termsToHighlight = [],
      onClose,
      onToggleNarrow,
      toolbarClassName,
    },
    forwardedRef
  ) => {
    const dispatch = useDispatch();
    const currentPage = useSelector(selectCurrentPage);
    const isPanelOpen = useSelector(selectIsPanelOpen);
    const totalPages = useSelector(selectTotalPages);

    const [searchBy, setSearchBy] = useState("");
    const virtualizerRef = useRef(null);

    const isFile = file instanceof File;
    const fileName = isFile ? file.name : file?.url;
    const extension = getFileExtension(fileName);

    useEffect(() => {
      dispatch(resetDocumentState());
      setSearchBy("");

      return () => {
        dispatch(resetDocumentState());
      };
    }, [extension, file]);

    const onLoadSuccess = (data) => {
      const total = data?.totalPages || 0;

      if (!showNavigation || total <= 1) {
        dispatch(setIsPanelOpen(false));
      }
      dispatch(setDocumentMetadata(data));
    };

    const renderDocument = () => {
      const params = { file, onLoadSuccess, ref: virtualizerRef };
      switch (extension) {
        case "docx":
        case "doc":
          return <DocXDocument {...params} />;
        case "pdf":
          return <PdfDocument {...params} />;
        default:
          return null;
      }
    };

    const onChangePage = async (newPage) => {
      const { scrollToCenter } = virtualizerRef?.current || {};
      if (!scrollToCenter) {
        return;
      }

      scrollToCenter(Math.max(0, newPage - 1));
    };

    // Expose utility functions or behaviours to
    // the parent app or Component using this one.
    useImperativeHandle(forwardedRef, () => {
      const virtualizer = virtualizerRef?.current?.virtualizer;
      return {
        getCurrentPage: () => currentPage,
        getVirtualIndexes: () =>
          virtualizer ? virtualizer.getVirtualIndexes() : [],
        getVirtualizer: () => virtualizer,
        scrollToCenter: virtualizerRef?.current?.scrollToCenter,
        totalPages,
      };
    }, [currentPage, fileName]);

    const contextValue = useMemo(
      () => ({
        fileName,
        searchBy,
        setSearchBy,
        termsToHighlight,
      }),
      [fileName, searchBy, setSearchBy, termsToHighlight]
    );

    return (
      <DocumentViewerContext.Provider value={contextValue}>
        <div className={clsx(classes["vt_document_viewer"], className)}>
          {showHeader && (
            <Toolbar
              canToggleNarrow={canToggleNarrow}
              fileName={fileName}
              isNarrow={isNarrow}
              onChangePage={onChangePage}
              onClose={onClose}
              onToggleNarrow={onToggleNarrow}
              showFileName={showFileName}
              showNavigation={showNavigation}
              toolbarClassName={toolbarClassName}
            />
          )}

          <div className={classes["vt_document_viewer__container"]}>
            {showNavigation && isPanelOpen && !isNarrow && (
              <PageNavigation onClickPage={onChangePage} />
            )}

            <div
              className={classes["vt_document_viewer__file_content"]}
              data-clarity-mask="True"
            >
              {renderDocument()}
            </div>
          </div>
        </div>
      </DocumentViewerContext.Provider>
    );
  }
);

DocumentViewer.propTypes = {
  canToggleNarrow: bool,
  className: string,
  file: FILE_PROP_TYPE,
  isNarrow: bool,
  showFileName: bool,
  showHeader: bool,
  showNavigation: bool,
  termsToHighlight: array,
  onClose: func,
  onToggleNarrow: func,
  toolbarClassName: string,
};

DocumentViewer.displayName = "DocumentViewer";

export default memo(DocumentViewer);
